import { ref } from 'vue'
import { useAvailability } from '../data/useAvailability.js'

/**
 * Composable for managing employee availability data in the sourcing context.
 * Uses the shared useAvailability composable for data loading and calculations.
 * @param {Function} getPreferredEmployees - Function to parse preferred employees from preference
 */
export function useSourcingAvailability(getPreferredEmployees) {
  // Use shared availability composable
  const { getWeeklyAvailability, calculateMatchStats, getMatchColor, getMatchBgColor } =
    useAvailability()

  // ==================== State ====================

  // Weekly availability cache per prospect: { prospectId: { 'empId-dpw': { matchPercent, avgAvailableDays } } }
  const prospectAvailability = ref({})

  // Full employee availability data with weeks for charts: { prospectId: { empId: employeeWithWeeks } }
  const employeeAvailabilityData = ref({})

  // ==================== Loading Functions ====================

  /**
   * Load availability for all preferred employees in a prospect
   * @param {Object} prospect
   * @param {Array} preferences
   * @param {string} [mode] - Availability mode ('definitief', 'presourcing', 'voorgesteld')
   */
  async function loadAvailabilityForProspect(prospect, preferences, mode) {
    if (!prospect.start_date || !prospect.expected_closing_date) return

    const startDate = prospect.start_date.split('T')[0]
    const endDate = prospect.expected_closing_date.split('T')[0]

    // Collect all preferred employee IDs
    const employeeIds = new Set()
    for (const pref of preferences) {
      const prefEmployees = getPreferredEmployees(pref)
      prefEmployees.forEach(id => employeeIds.add(id))
    }

    if (employeeIds.size === 0) return

    try {
      // Use shared availability loading
      const weeklyData = await getWeeklyAvailability(startDate, endDate, false, mode)

      if (!weeklyData?.length) return

      // Calculate match stats per employee per preference DPW
      const availabilityMap = {}
      const employeeDataMap = {}

      for (const pref of preferences) {
        const dpw = parseFloat(pref.dpw) || 0
        if (dpw <= 0) continue

        const prefEmployees = getPreferredEmployees(pref)
        for (const empId of prefEmployees) {
          const empData = weeklyData.find(e => e.simplicate_id === empId)
          if (!empData?.weeks) continue

          // Store full employee data for charts
          if (!employeeDataMap[empId]) {
            employeeDataMap[empId] = empData
          }

          // Use shared match calculation
          const stats = calculateMatchStats(empData.weeks, dpw, empId)

          // Store with simple key for lookup
          const simpleKey = `${empId}-${dpw}`
          if (!availabilityMap[simpleKey]) {
            availabilityMap[simpleKey] = stats
          }
        }
      }

      prospectAvailability.value[prospect.id] = availabilityMap
      employeeAvailabilityData.value[prospect.id] = employeeDataMap
    } catch (e) {
      console.error(`Error loading availability for prospect ${prospect.id}:`, e)
    }
  }

  /**
   * Load availability for a specific employee (used when adding custom employee)
   * @param {Object} prospect
   * @param {Object} pref
   * @param {string} empId
   * @param {string} [mode] - Availability mode ('definitief', 'presourcing', 'voorgesteld')
   */
  async function loadAvailabilityForEmployee(prospect, pref, empId, mode) {
    if (!prospect.start_date || !prospect.expected_closing_date) return

    const startDate = prospect.start_date.split('T')[0]
    const endDate = prospect.expected_closing_date.split('T')[0]
    const dpw = parseFloat(pref.dpw) || 0
    if (dpw <= 0) return

    try {
      // Use shared availability loading
      const weeklyData = await getWeeklyAvailability(startDate, endDate, false, mode)

      if (!weeklyData?.length) return

      const empData = weeklyData.find(e => e.simplicate_id === empId)
      if (!empData?.weeks) return

      // Store full employee data for charts
      if (!employeeAvailabilityData.value[prospect.id]) {
        employeeAvailabilityData.value[prospect.id] = {}
      }
      employeeAvailabilityData.value[prospect.id][empId] = empData

      // Use shared match calculation
      const stats = calculateMatchStats(empData.weeks, dpw, empId)

      // Store availability
      if (!prospectAvailability.value[prospect.id]) {
        prospectAvailability.value[prospect.id] = {}
      }
      const simpleKey = `${empId}-${dpw}`
      prospectAvailability.value[prospect.id][simpleKey] = stats
    } catch (e) {
      console.error(`Error loading availability for employee ${empId}:`, e)
    }
  }

  // ==================== Getter Functions ====================

  /**
   * Get match stats for an employee in a specific prospect/preference context
   * @param {number} prospectId
   * @param {Object} pref
   * @param {string} empId
   * @returns {Object|null}
   */
  function getEmployeeMatchStats(prospectId, pref, empId) {
    const availability = prospectAvailability.value[prospectId]
    if (!availability) return null

    const dpw = parseFloat(pref.dpw) || 0
    const simpleKey = `${empId}-${dpw}`
    return availability[simpleKey] || null
  }

  /**
   * Get employee with weeks data for chart
   * @param {number} prospectId
   * @param {string} empId
   * @returns {Object|null}
   */
  function getEmployeeWithWeeks(prospectId, empId) {
    return employeeAvailabilityData.value[prospectId]?.[empId] || null
  }

  // ==================== Utility Functions ====================

  /**
   * Clear availability data for a prospect
   * @param {number} prospectId
   */
  function clearProspectAvailability(prospectId) {
    delete prospectAvailability.value[prospectId]
    delete employeeAvailabilityData.value[prospectId]
  }

  /**
   * Clear all availability data
   */
  function clearAllAvailability() {
    prospectAvailability.value = {}
    employeeAvailabilityData.value = {}
  }

  // ==================== Return ====================

  return {
    // State
    prospectAvailability,
    employeeAvailabilityData,

    // Loading functions
    loadAvailabilityForProspect,
    loadAvailabilityForEmployee,

    // Getter functions
    getEmployeeMatchStats,
    getEmployeeWithWeeks,

    // Styling helpers (from shared composable)
    getMatchColor,
    getMatchBgColor,

    // Utility functions
    clearProspectAvailability,
    clearAllAvailability
  }
}
